/*
 * IMPORTS
 */
import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
  useStyleConfig,
} from "@chakra-ui/react";
import { HiArrowPath, HiCursorArrowRays } from "react-icons/hi2";
import _ from "underscore";
import { Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import SmsReadFailedQuery from "./__query__/index.sms.read.failed.query";

/*
 * HELPERS
 */
const _NormalizeErrors = (__errors) => {
  const _seen = new Set();

  return _.compact(__errors).reduce((__acc, __error) => {
    `${__error}`.split(",").forEach((__value) => {
      const _value = __value?.trim();

      if (!_value || "NONE" === _value || _seen.has(_value)) return;

      _seen.add(_value);
      __acc.push(_value);
    });

    return __acc;
  }, []);
};

const _GetDlrStatusColor = (__status) =>
  Object.React.App.colors.SMS_STATUS[String(__status ?? "").toUpperCase()];

/*
 * OBJECTS
 */
function Index({ title }) {
  const _dispatch = useDispatch();
  const _historyRef = useHistory();

  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );

  React.useEffect(() => {
    return () => {
      _setFilterDebounced.cancel?.();
    };
  }, [_setFilterDebounced]);

  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;

  const _QuerySmsReadFailed = useQuery(SmsReadFailedQuery, {
    variables: {
      startDate: dateRange?.selection?.startDate,
      endDate: dateRange?.selection?.endDate,
      filter: _appliedFilter,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
    notifyOnNetworkStatusChange: true,
  });

  const _styles = useStyleConfig("Card");
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });
  const _tableHeaderHeightRef = React.useRef(0);

  // Handle refresh with loading state
  const _HandleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await _QuerySmsReadFailed.refetch();
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  };

  const _HandleErrorCodeClick = React.useCallback(
    (row) => {
      if (!row?.originalError) return;

      _dispatch({
        type: "PASS_ON_UPDATE",
        PassOn: {
          companyName: void 0,
          error: row.originalError,
          startDate: dateRange?.selection.startDate,
          endDate: dateRange?.selection.endDate,
          _navigatedToMessage: true,
        },
      });
      _historyRef.push("/reports/message");
    },
    [
      _dispatch,
      _historyRef,
      dateRange?.selection.endDate,
      dateRange?.selection.startDate,
    ],
  );

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 40,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "status",
        headerName: "Status",
        align: "center",
        headerAlign: "center",
        width: 140,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: ({ value }) => (
          <Chip
            label={value ?? "-"}
            size="small"
            sx={{
              backgroundColor: `${_GetDlrStatusColor(value)} !important`,
              color: "white !important",
              height: "20px",
              width: "fit-content",
              maxWidth: "100%",
              padding: 0,
              "& .MuiChip-label": {
                padding: "0 6px !important",
                fontSize: "12px !important",
                fontWeight: "300 !important",
              },
            }}
          />
        ),
      },
      {
        field: "error",
        headerName: "Error Code",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: ({ row, value }) =>
          row?.originalError ? (
            <Button
              aria-label={`View messages for ${value}`}
              title={value}
              variant="ghost"
              size="sm"
              maxW="100%"
              minH="24px"
              h="auto"
              px="6px"
              py="2px"
              borderRadius="6px"
              bg="transparent"
              color="inherit"
              cursor="pointer"
              fontFamily="inherit"
              fontSize="13px"
              fontWeight="400"
              lineHeight="1.25"
              whiteSpace="normal"
              wordBreak="break-word"
              textAlign="center"
              rightIcon={<HiCursorArrowRays size="12px" />}
              transition="color 0.15s ease"
              _hover={{
                bg: "transparent",
                textDecoration: "underline",
              }}
              _active={{
                bg: "transparent",
              }}
              _focusVisible={{
                boxShadow: "0 0 0 2px var(--chakra-colors-purple-300)",
              }}
              onClick={() => _HandleErrorCodeClick(row)}
            >
              {value}
            </Button>
          ) : (
            <Text color="gray.500" fontSize="13px">
              {value}
            </Text>
          ),
      },
      {
        field: "count",
        headerName: "Count",
        align: "center",
        headerAlign: "center",
        width: 90,
        editable: false,
        sortable: true,
        disableColumnMenu: true,
      },
    ],
    [_HandleErrorCodeClick],
  );

  // Define rows
  const rows = React.useMemo(() => {
    if (!_QuerySmsReadFailed?.data?.SmsReadFailed?.data) return [];

    return _QuerySmsReadFailed.data.SmsReadFailed.data.map((item, index) => {
      const _error = _NormalizeErrors([item?.error]).join(", ");
      const _hasError = Boolean(_error);

      return {
        id: `error-${index}-${item?._count?._all || 0}`,
        sno: `${index + 1}.`,
        status: item?.status ?? "-",
        error: _hasError ? _error : "No error/DLR received from vendor",
        originalError: _hasError ? _error : void 0,
        count: item?._count?._all ?? 0,
      };
    });
  }, [_QuerySmsReadFailed?.data?.SmsReadFailed?.data]);

  // Return component.
  return (
    <Box
      __css={_styles}
      display="flex"
      flexDirection="column"
      w="100%"
      p={_isCurrentViewMobile ? "12px" : "22px"}
      gap={_isCurrentViewMobile ? "12px" : "22px"}
      fontSize={["clamp(12px, 1.5vw, 15px)"]}
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.17)"
    >
      <Flex justify="space-between" alignItems="center" width="100%">
        <Text
          color="rgba(43,54,116,1)"
          fontWeight="700"
          fontSize={{ base: "sm", md: "md" }}
          textAlign="left"
        >
          {title ?? "ERROR CODE"}
        </Text>
        <Flex alignItems="center" gap="8px">
          <DateRangePicker
            startDate={dateRange?.selection?.startDate}
            endDate={dateRange?.selection?.endDate}
            w="100%"
            fontSize="13px"
            setSelectDate={setDateRange}
          />
          <IconButton
            display={{ base: "flex", md: "none" }}
            aria-label="Refresh"
            icon={<HiArrowPath size="18px" />}
            onClick={_HandleRefresh}
            variant="ghost"
            color="purple"
            isDisabled={isRefreshing || _QuerySmsReadFailed.loading}
            _hover={{ bg: "purple.100" }}
          />
          <Button
            display={{ base: "none", md: "flex" }}
            borderRadius="8px"
            fontWeight="450"
            size="sm"
            minW="20px"
            backgroundColor="purple.200"
            color="purple"
            _hover={{ bg: "purple.800", color: "white" }}
            onClick={_HandleRefresh}
            isDisabled={isRefreshing}
            leftIcon={<HiArrowPath />}
          >
            Refresh
          </Button>
        </Flex>
      </Flex>
      <Box width="100%" height="430px">
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={45}
          hideFooter
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={_setFilterDebounced}
          sx={{
            "& .MuiDataGrid-main": {
              borderBottomLeftRadius: "12px",
              borderBottomRightRadius: "12px",
              overflow: "hidden",
            },
          }}
          slots={{
            loadingOverlay: () => (
              <TableSpinner
                isLoading={true}
                isEmpty={false}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noResultsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noRowsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          loading={_QuerySmsReadFailed?.loading || isRefreshing}
        />
      </Box>
    </Box>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {
  title: PropTypes.string,
};

/*
 * EXPORTS
 */
export default Index;
